import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltIconArrowRightSolid } from '@cae/cobalt-react/icons/solid/CobaltIconArrowRightSolid';
import { useLogin } from '@pages/landing/hooks/use-login/useLogin';
import { navigateToHelp } from '@shared/helpers/navigate-to-help/navigateToHelp';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import type { LandingCarouselContentProps } from './LandingCarouselContent.types';

import styles from './LandingCarouselContent.module.css';
import {
  CAROUSEL_CONTENT,
  handleOpenCrewTraining,
} from './LandingCarouselContent.utils';

const LandingCarouselContent = React.memo(
  ({
    activeTab,
  }: Readonly<LandingCarouselContentProps>): React.ReactElement => {
    const { t } = useTranslation();
    const { handleLogin, handleEmployeeLogin } = useLogin();
    const { trainSectionIsAvailableLandingPage } = useFlags();
    const content = CAROUSEL_CONTENT[activeTab];

    const handleHelpClick = React.useCallback((): void => {
      navigateToHelp(true);
    }, []);

    const showLoginSection =
      activeTab !== 'train' || trainSectionIsAvailableLandingPage;

    return (
      <div
        role="tabpanel"
        id={`carousel-panel-${activeTab}`}
        className={styles.landingCarouselContent}
        data-testid="landing-carousel-content"
      >
        <div className={styles.landingCarouselContentImage}>
          <img
            src={content.imageSrc}
            alt={t(content.imageAltKey)}
            className={styles.landingCarouselContentImg}
          />
        </div>

        <div className={styles.landingCarouselContentInfo}>
          <div className={styles.landingCarouselContentModuleRow}>
            <img
              src={content.iconSrc}
              alt=""
              aria-hidden="true"
              className={styles.landingCarouselContentIcon}
            />
            <div className={styles.landingCarouselContentModuleLabelStack}>
              <h3 className={`${styles.landingCarouselContentModule} heading-02`}>
                {t(content.moduleLabelKey)}
              </h3>
              {activeTab === 'train' && content.moduleLabelDescriptionKey && (
                <span
                  className={`${styles.landingCarouselContentModuleDescription} ${styles.landingCarouselContentModuleDescriptionInline} body-01`}
                  data-testid="landing-carousel-module-description"
                >
                  {t(content.moduleLabelDescriptionKey)}
                </span>
              )}
            </div>
          </div>

          {activeTab === 'train' && content.moduleLabelDescriptionKey && (
            <span
              className={`${styles.landingCarouselContentModuleDescription} ${styles.landingCarouselContentModuleDescriptionBlock}`}
              data-testid="landing-carousel-module-description-mobile"
            >
              {t(content.moduleLabelDescriptionKey)}
            </span>
          )}

          <h4
            className={`${styles.landingCarouselContentTitle} heading-01`}
            data-testid="landing-carousel-content-title"
          >
            {t(content.titleKey)
              .split('\n')
              .map((line, idx) => (
                <React.Fragment key={idx}>
                  {line}
                  {idx < t(content.titleKey).split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
          </h4>

          <ul className={styles.landingCarouselContentList}>
            {content.bulletKeys.map(key => (
              <li key={key} className={`${styles.landingCarouselContentListItem} body-02`}>
                {t(key)}
              </li>
            ))}
          </ul>

          {showLoginSection && (
            <div className={styles.landingCarouselContentButtonWrapper}>
              <CobaltButton
                type="button"
                variant="primary"
                onClick={
                  activeTab === 'instruct' ? handleEmployeeLogin : handleLogin
                }
                className={styles.landingCarouselContentButton}
                aria-label={t('common.login')}
              >
                {t('common.login')}
                <CobaltIconArrowRightSolid size="medium" />
              </CobaltButton>

              {activeTab === 'train' && content.alsoAvailableInIOS && (
                <p className={styles.landingCarouselContentAlsoAvailable}>
                  {t(content.alsoAvailableInIOS)}
                </p>
              )}
              <div className={styles.landingCarouselContentContactWrapper}>
                <p className={`${styles.landingCarouselContentContact} body-02`}>
                  {content.moduleContact ? t(content.moduleContact) : ''}
                </p>

                {activeTab === 'manage' ? (
                  <p className={styles.landingCarouselContentContactMore}>
                    {content.moduleContactMore
                      ? t(content.moduleContactMore)
                      : ''}
                  </p>
                ) : activeTab === 'train' &&
                  trainSectionIsAvailableLandingPage &&
                  content.moduleContactMore ? (
                  <button
                    type="button"
                    className={`${styles.landingCarouselContentContactMoreInteractive} body-02`}
                    onClick={handleHelpClick}
                    data-testid="landing-carousel-module-contact-link"
                  >
                    {t(content.moduleContactMore)}
                  </button>
                ) : null}
              </div>
            </div>
          )}

          {!showLoginSection && (
            <div className={styles.landingCarouselContentContactWrapper}>
              <p className={styles.landingCarouselContentContact}>
                {content.availableSoon ? t(content.availableSoon) : ''}
              </p>
              <button
                type="button"
                className={styles.landingCarouselContentContactMoreInteractive}
                onClick={handleOpenCrewTraining}
                data-testid="landing-carousel-module-contact-link"
              >
                {content.goToCrewTraining ? t(content.goToCrewTraining) : ''}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
);

LandingCarouselContent.displayName = 'LandingCarouselContent';

export { LandingCarouselContent };
